import React, { useCallback, useState } from 'react';
import { Box, Typography, useTheme } from '@material-ui/core';
import {
  ACCEPTED_FILES_CONFIG,
  ALLOWED_EXTENSIONS,
  MAX_FILE_SIZE,
  MAX_FILES_COUNT,
  FILE_VALIDATION_ERRORS,
} from '../constants';
import CloseIcon from '@material-ui/icons/Close';
import { FileRejection, useDropzone } from 'react-dropzone';
import {
  ChipsContainer,
  StyledChip,
  DropzoneContainer,
  DropzoneWrapper,
} from './FileAttachmentStyles';

type FileAttachmentProps = {
  attachments: FileList | null;
  onAttachmentsChange: (attachments: FileList | null) => void;
};

const FileAttachment = ({
  attachments,
  onAttachmentsChange,
}: FileAttachmentProps) => {
  const { palette } = useTheme();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateFiles = (files: File[]): string | null => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > MAX_FILE_SIZE) {
        return FILE_VALIDATION_ERRORS.FILE_TOO_LARGE;
      }

      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
        return FILE_VALIDATION_ERRORS.FILE_TYPE_NOT_ALLOWED;
      }
    }
    return null;
  };

  const handleRemoveFile = (indexToRemove: number) => {
    if (attachments) {
      const newFiles = Array.from(attachments);
      newFiles.splice(indexToRemove, 1);

      const dataTransfer = new DataTransfer();
      newFiles.forEach(file => dataTransfer.items.add(file));

      onAttachmentsChange(dataTransfer.files);
      setValidationError(null);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const existingFiles = attachments ? Array.from(attachments) : [];
      const availableSlots = Math.max(0, MAX_FILES_COUNT - existingFiles.length);

      const filesToAdd = acceptedFiles.slice(0, availableSlots);
      const isExceeded = acceptedFiles.length > filesToAdd.length;

      const dataTransfer = new DataTransfer();
      [...existingFiles, ...filesToAdd].forEach(file => dataTransfer.items.add(file));

      onAttachmentsChange(dataTransfer.files);
      setValidationError(isExceeded ? FILE_VALIDATION_ERRORS.TOO_MANY_FILES : null);
    },
    [attachments, onAttachmentsChange],
  );

  const onDropRejected = useCallback((rejectedFiles: FileRejection[]) => {
    const fileError = validateFiles(rejectedFiles.map(f => f.file));
    if (fileError) {
      setValidationError(fileError);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept: ACCEPTED_FILES_CONFIG,
    multiple: true,
    validator: file => {
      if (!file || !file.name) {
        return {
          code: 'file-invalid',
          message: FILE_VALIDATION_ERRORS.INVALID_FILE,
        };
      }

      if (file.size > MAX_FILE_SIZE) {
        return {
          code: 'file-too-large',
          message: FILE_VALIDATION_ERRORS.FILE_TOO_LARGE,
        };
      }

      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension)) {
        return {
          code: 'file-invalid-type',
          message: FILE_VALIDATION_ERRORS.FILE_TYPE_NOT_ALLOWED,
        };
      }

      return null;
    },
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });

  const getBorderColor = () => {
    if (validationError) return palette.error.main;
    if (isDragging) return palette.primary.main;
    return palette.divider;
  };

  const getBackgroundColor = () => {
    if (validationError) return palette.error.light;
    if (isDragging) return palette.action.hover;
    return palette.background.default;
  };

  return (
    <DropzoneWrapper>
      {attachments && attachments.length > 0 && (
        <Box style={{ marginBottom: 4 }}>
          <Typography
            variant="body2"
            style={{ fontWeight: 'bold', marginBottom: 4 }}
          >
            Attached Files:
          </Typography>
          <ChipsContainer>
            {Array.from(attachments).map((file, index) => (
              <StyledChip
                key={`${file.name}-${index}`}
                label={file.name}
                onDelete={() => handleRemoveFile(index)}
                deleteIcon={<CloseIcon />}
                variant="outlined"
              />
            ))}
          </ChipsContainer>
        </Box>
      )}

      {validationError && (
        <Typography variant="body2" color="error" style={{ marginBottom: 4 }}>
          {validationError}
        </Typography>
      )}

      <DropzoneContainer
        {...getRootProps()}
        style={{
          borderColor: getBorderColor(),
          backgroundColor: getBackgroundColor(),
        }}
      >
        <input {...getInputProps()} />
        <Typography
          variant="body1"
          style={{ fontWeight: 'bold', marginBottom: 4 }}
        >
          {isDragging
            ? 'DROP FILES HERE'
            : 'CLICK HERE TO BROWSE YOUR DEVICE OR DROP FILES TO UPLOAD'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Max file size is 33 MB with accepted file formats of:
          {ALLOWED_EXTENSIONS.map(ext => ext.toUpperCase()).join(', ')}.
        </Typography>
      </DropzoneContainer>
    </DropzoneWrapper>
  );
};

export default FileAttachment;
